import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useClickAway } from 'react-use';
import googleSheets from '@/config/googleSheets';
import templateEngine, { hasTrimDecDirective, templateVariableStringToMap } from '@/utils/templateEngine';
import FormatControls, { DecimalFormatControls } from '@/components/Editor/Form/fields/FormatControls';
import { isFormatNonAbbreviatable, getDecimalFromPattern, isPatternHasDecimal } from '@/utils/spreadsheetFormat';
import Input from '@/components/FormElements/Input';
import Select from '@/components/FormElements/Select';
import RevealPill from '@/components/Editor/Form/fields/RevealPill';
import useSSFFormats from './useSSFFormats';
import * as formatUtils from './formatUtils';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

// Standalone template input, opens revealed pill
const TemplateRevealPill = props => {
  const {
    label = '',
    template,
    openButtonVariant
  } = props;
  const {
    ssfArgument,
    closestMatchFormat
  } = useSSFFormats({
    template
  });
  return /*#__PURE__*/_jsx(RevealPill, {
    label: label,
    openButtonVariant: openButtonVariant,
    children: /*#__PURE__*/_jsx(TemplateInput, _objectSpread({
      activeFormatValue: ssfArgument,
      closestMatchFormat: closestMatchFormat
    }, props))
  });
};

const TemplateSection = props => {
  const {
    template
  } = props;
  const {
    ssfArgument,
    closestMatchFormat
  } = useSSFFormats({
    template
  });
  return /*#__PURE__*/_jsx(TemplateInput, _objectSpread({
    activeFormatValue: ssfArgument,
    closestMatchFormat: closestMatchFormat
  }, props));
};

const TemplateInput = _ref => {
  let {
    id,
    template,
    exampleValue,
    activeFormatValue,
    closestMatchFormat,
    forceDecimalFormattingOnly,
    onChange
  } = _ref;
  const {
    0: isManualUpdate,
    1: setIsManualUpdate
  } = useState(false);
  const {
    0: previewError,
    1: setPreviewError
  } = useState();
  const manualInputRef = useRef(null);
  useClickAway(manualInputRef, () => {
    setIsManualUpdate(false);
  }); // Determine if template changes can be compiled
  // and then submit any valid template chnages

  const validateChange = useCallback(tmpl => {
    try {
      templateEngine(tmpl, {
        value: exampleValue
      });
    } catch (err) {
      setPreviewError(err);
      return false;
    }

    setPreviewError(undefined);
    return true;
  }, [exampleValue, setPreviewError]); // Prevent form submittions from custom
  // template updates field

  const handleInputSubmit = useCallback(evt => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
    }
  }, []);
  const handleChange = useCallback(options => {
    if (!options) {
      onChange(null);
      return;
    }

    const {
      ssf,
      trimDec,
      abbr
    } = options || {};
    let result = template || `{{value}}`; // Upsert or remove ssf formatting directive

    if (typeof ssf !== 'undefined') {
      result = ssf ? formatUtils.upsertSsfArg(result, ssf) : formatUtils.removeSsfArg(result);
    } // Add or remove trim_dec formatting directive
    // and ensure that SSF contains fixed 2 decimal places


    if (typeof trimDec === 'boolean') {
      // Toggle on/off trim_dec
      result = trimDec ? formatUtils.addTrimDec(result) : formatUtils.removeTrimDec(result);
    } // Upsert or remove abbreviation directive


    if (typeof abbr !== 'undefined') {
      // Add abbreviation to template
      result = abbr ? formatUtils.addAbbr(result, abbr) : formatUtils.removeAbbr(result);
    } // Only submit template update that compiles sucessfully


    const isValid = validateChange(result);
    if (isValid) onChange(result);
  }, [template, validateChange, onChange]); // "Auto" decimal trimming behavior, or trim_dec
  // determines if this template has it enabled

  const hasTrimDec = useMemo(() => template ? hasTrimDecDirective(template) : false, [template]); // Lookup the current abbreviation symbol
  // from the template, if any exists

  const abbrSymbol = useMemo(() => {
    var _varMap$get;

    const varMap = templateVariableStringToMap(template || '');
    const abbrArgs = ((_varMap$get = varMap.get('abbr')) === null || _varMap$get === void 0 ? void 0 : _varMap$get.split(' ')) || [];
    return abbrArgs[0] || '';
  }, [template]); // Get the fixed decimal length from the active format value
  // if automatic decimal trimming is not enabled

  const fixedDecimalLength = useMemo(() => {
    if (hasTrimDec) return -1;
    const isDecimal = Boolean(closestMatchFormat && isPatternHasDecimal((closestMatchFormat === null || closestMatchFormat === void 0 ? void 0 : closestMatchFormat.value) || ''));
    return isDecimal ? getDecimalFromPattern(activeFormatValue || '') : -1;
  }, [hasTrimDec, activeFormatValue, closestMatchFormat]);
  return /*#__PURE__*/_jsx("div", {
    className: styles.container,
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.controls,
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.control,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.label,
          children: "Format"
        }), /*#__PURE__*/_jsx(FormatControls, {
          editable: !isManualUpdate && !forceDecimalFormattingOnly,
          closestMatchFormat: closestMatchFormat,
          activeFormatValue: activeFormatValue,
          isManualUpdate: isManualUpdate,
          onToggleManualUpdate: () => setIsManualUpdate(!isManualUpdate),
          onChange: ssfFormat => {
            handleChange({
              ssf: ssfFormat,
              abbr: isFormatNonAbbreviatable(ssfFormat) ? null : undefined
            });
          }
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.control,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.label,
          children: "Abbreviation"
        }), /*#__PURE__*/_jsxs(Select, {
          className: styles.select,
          value: abbrSymbol,
          disabled: forceDecimalFormattingOnly,
          onChange: evt => {
            var _evt$currentTarget;

            handleChange({
              ssf: formatUtils.useOrCreateAbbreviatableSsfFormat(template || ''),
              abbr: `${(_evt$currentTarget = evt.currentTarget) === null || _evt$currentTarget === void 0 ? void 0 : _evt$currentTarget.value} ${hasTrimDec ? '' : fixedDecimalLength > -1 ? fixedDecimalLength : ''}`.trim() || null
            });
          },
          children: [/*#__PURE__*/_jsx("option", {
            value: "auto",
            children: "Auto"
          }), /*#__PURE__*/_jsx("option", {
            value: "",
            children: "None"
          }), /*#__PURE__*/_jsx("option", {
            value: "k",
            children: "k (Thousands)"
          }), /*#__PURE__*/_jsx("option", {
            value: "m",
            children: "m (Millions)"
          }), /*#__PURE__*/_jsx("option", {
            value: "b",
            children: "b (Billions)"
          })]
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.control,
        children: [/*#__PURE__*/_jsx("span", {
          className: styles.label,
          children: "Decimal Places"
        }), /*#__PURE__*/_jsx(DecimalFormatControls, {
          editable: !isManualUpdate && !forceDecimalFormattingOnly,
          fixedDecimalLength: fixedDecimalLength,
          activeTrimDecimal: hasTrimDec,
          onChangeDecimal: value => {
            // Default to 2 decimal places if auto trim disabling
            const decimalPlaces = hasTrimDec ? 2 : value; // Add fixed decimal places to any existing abbreviation settings

            const abbr = abbrSymbol ? `${abbrSymbol} ${decimalPlaces}` : null;
            const ssf = formatUtils.createNewSsfFormatDecimal(template || '', decimalPlaces);
            handleChange({
              ssf,
              abbr,
              trimDec: false
            });
          },
          onEnableTrimDecimal: () => {
            // Add auto decimal places to any existing abbreviation settings
            const abbr = abbrSymbol ? `${abbrSymbol}` : null;
            const ssf = formatUtils.createNewSsfFormatDecimal(template || '', 2);
            handleChange({
              ssf,
              abbr,
              trimDec: true
            });
          }
        })]
      }), isManualUpdate ? /*#__PURE__*/_jsx(Input, {
        id: id,
        ref: manualInputRef,
        className: styles.manualInput,
        placeholder: googleSheets.formats.number,
        defaultValue: activeFormatValue || '',
        hasError: Boolean(previewError),
        onKeyDown: handleInputSubmit,
        onChange: evt => handleChange({
          ssf: `${evt.target.value || ''}`.trim(),
          trimDec: false,
          abbr: null
        })
      }) : /*#__PURE__*/_jsx(_Fragment, {})]
    })
  });
};

export { TemplateRevealPill, TemplateSection };